import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../../api/client';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../../components/AppDataGrid';
import { AppDialog } from '../../../components/AppDialog';
import { AppToolbar } from '../../../components/AppToolbar';
import { useConfirm } from '../../../components/confirm';
import { useNotify } from '../../../components/notifications';
import { fmtLocalDate } from '../../../utils/datetime';
import { PickListDialog } from './PickListDialog';
import {
  PARAM_TABS, TAB_COLUMNS, pendingRow, rowsOfTab,
  type GridRow, type Option, type ParamCol, type ParamKind, type TabId,
} from './testParamTabs';

/** One buffered change of a row: what is sent (wire) and what the grid shows (display). */
export interface RowEdit { wire: Record<string, string | null>; display: Record<string, unknown> }

const errText = (e: unknown, fallback: string) =>
  (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ?? fallback;

interface PickParam extends Record<string, unknown> { id: string; name: string | null; code: string | null }
const PICK_COLUMNS: AppColumn<PickParam>[] = [{ field: 'name', header: 'Parameter' }];

/**
 * G5 "Test Parameter Defaults": Sample Parameters · Surrogates · Internal Standards ·
 * QC Sample Parameters · Component · QC Parameter Defaults. QC Sample Parameters lists EVERY QC type's
 * rows (with a QCType column) instead of G5's one-type-at-a-time QC Types dropdown (owner request 2026-10-16);
 * Add asks for the QC type only when the test has more than one.
 * Each grid's bar is Refresh · Add · Remove · Export to (G5: ADD; G6 uses title case) (QC Parameter Defaults has no
 * ADD / Remove, as in G5). Cell edits and ADDed rows are held by the popup and stored by
 * its OK; Remove — and everything on the Component tab — takes effect at once, as in G5.
 */
export function TestParamDefaults({
  testId, rows, edits, units, qcTypes, onCellEdit, onAdd, onDropPending, onReload,
}: {
  testId: string;
  rows: GridRow[];
  edits: Record<string, RowEdit>;
  units: Option[];
  /** The test's QC types except Sample (Method Defaults ▸ QCTypes) — the type a QC parameter is added under. */
  qcTypes: Option[];
  onCellEdit: (row: GridRow, col: ParamCol, value: string) => void;
  onAdd: (rows: GridRow[]) => void;
  onDropPending: (keys: Set<string>) => void;
  onReload: () => Promise<void>;
}) {
  const notify = useNotify();
  const { confirm } = useConfirm();
  const [tab, setTab] = useState<TabId>('sample');
  // The QC type an Add on QC Sample Parameters puts its parameters under (chosen in the QC Type step).
  const [qcType, setQcType] = useState<string>('');
  const [choosingQc, setChoosingQc] = useState(false);
  const [selected, setSelected] = useState<Set<string | number>>(new Set());
  const [picking, setPicking] = useState<ParamKind | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const seq = useRef(0);

  useEffect(() => { setSelected(new Set()); }, [tab]);

  const isNames = useMemo(() => rows.filter((r) => r.internalStandard && !r.pending).map((r) => String(r.parameter ?? '')), [rows]);
  const unitNames = useMemo(() => units.map((u) => u.name ?? ''), [units]);

  const shown = useMemo(
    () => rowsOfTab(rows, tab).map((r) => (edits[r.key] ? { ...r, ...edits[r.key]!.display } : r)),
    [rows, tab, edits],
  );

  const columns: AppColumn<GridRow>[] = useMemo(() => {
    if (tab === 'component') return [];
    return TAB_COLUMNS[tab].map((c): AppColumn<GridRow> => {
      const base: AppColumn<GridRow> = { field: c.field, header: c.header, width: c.width };
      if (c.edit === 'date') {
        return { ...base, value: (r) => r[c.field] ?? '', text: (r) => (r[c.field] ? fmtLocalDate(String(r[c.field])) : ''),
          edit: { type: 'date' } };
      }
      if (!c.edit) return base;
      const options = c.edit === 'unit' ? unitNames : c.edit === 'is' ? isNames : undefined;
      return {
        ...base,
        edit: {
          type: options ? 'combo' : c.edit === 'number' ? 'number' : 'text',
          options,
          clearable: !!options,
          // G5: a parameter with a default-result list takes its DefaultResult from that list, not typing.
          disabled: c.field === 'defaultResult' ? (r) => !!r.hasDefaultResults : undefined,
        },
      };
    });
  }, [tab, unitNames, isNames]);

  const kind = PARAM_TABS.find((t) => t.id === tab)?.kind;
  const qcOption = qcTypes.find((q) => q.name === qcType) ?? null;

  const refresh = async () => { setRefreshing(true); try { await onReload(); } finally { setRefreshing(false); } };

  const add = () => {
    if (!kind) return;
    if (kind === 'qc') {
      // A QC parameter belongs to a QC type: none on the test → nothing to add under; one → use it;
      // several → ask which (the QC Type step), then open the picker.
      if (qcTypes.length === 0) { notify.info('Add a QC type on the QCTypes tab first.'); return; }
      if (qcTypes.length === 1) { setQcType(qcTypes[0]!.name ?? ''); setPicking('qc'); return; }
      if (!qcTypes.some((q) => q.name === qcType)) setQcType(qcTypes[0]!.name ?? '');
      setChoosingQc(true);
      return;
    }
    setPicking(kind);
  };

  const picked = (ps: PickParam[]) => {
    // A parameter already waiting to be added on this tab is not added twice.
    // (On QC Sample Parameters only rows waiting under the SAME QC type count.)
    const waiting = new Set(rowsOfTab(rows, tab)
      .filter((r) => r.pending && (picking !== 'qc' || r.pending.qcTypeId === qcOption?.id))
      .map((r) => r.parameterId));
    const fresh = ps.filter((p) => !waiting.has(p.id))
      .map((p) => { seq.current += 1; return pendingRow(seq.current, picking!, p, qcOption ? { id: qcOption.id, name: qcOption.name ?? '' } : null); });
    onAdd(fresh);
    setPicking(null);
  };

  const remove = async () => {
    if (selected.size === 0) { notify.info('Select checkbox'); return; }
    const picks = shown.filter((r) => selected.has(r.key));
    const pend = new Set(picks.filter((r) => r.pending).map((r) => r.key));
    const saved = picks.filter((r) => !r.pending);
    if (!(await confirm({ title: 'Remove', tone: 'danger', confirmLabel: 'Remove',
      message: 'You are about to remove selected record(s). Do you want to proceed?' }))) return;
    onDropPending(pend);
    setSelected(new Set());
    if (saved.length === 0) return;
    try {
      const res = await api.post<{ data?: { blocked?: number; message?: string } }>(
        `/api/v1/test-methods/${testId}/parameters/remove`, { ids: saved.map((r) => r.oid) });
      const d = res.data?.data;
      if (d?.blocked) notify.error(d.message ?? 'The testparameter cannot be deleted! it is attached to a sample.');
      else notify.success(d?.message ?? 'Removed Successfully');
    } catch (e) {
      notify.error(errText(e, 'The parameters could not be removed.'));
    }
    await onReload();
  };

  const tabTitle = PARAM_TABS.find((t) => t.id === tab)!.title;

  return (
    <div className="tmd-section">
      <h3 className="tmv-h">Test Parameter Defaults</h3>
      <div className="tmv-tabs" role="tablist" aria-label="Test Parameter Defaults">
        {PARAM_TABS.map((t) => (
          <button key={t.id} type="button" role="tab" aria-selected={tab === t.id} className={tab === t.id ? 'on' : ''}
            onClick={() => setTab(t.id)}>{t.title}</button>
        ))}
      </div>
      <div className="tmv-panel">
        {tab === 'component' ? (
          <ComponentTab testId={testId} />
        ) : (
          <>
            <AppToolbar
              noHotkeys
              showMeta={false}
              refreshFirst
              onRefresh={() => { void refresh(); }}
              refreshing={refreshing}
              {...(kind ? {
                crudFirst: true, newLabel: 'Add', onNew: add,
                deleteLabel: 'Remove', onDelete: () => { void remove(); },
              } : {})}
              exportData={{ filename: tabTitle, ...gridExportMatrix(shown, columns) }}
            />
            <AppDataGrid<GridRow>
              rows={shown}
              columns={columns}
              getRowId={(r) => r.key}
              selectable={!!kind}
              selectedIds={selected}
              onSelectionChange={setSelected}
              onCellCommit={(row, col, v) => {
                const c = TAB_COLUMNS[tab as Exclude<TabId, 'component'>].find((x) => x.field === col.field);
                if (c) onCellEdit(row, c, v == null ? '' : String(v));
              }}
              getRowClassName={(r) => (r.pending ? 'clp-row-new' : undefined)}
              defaultPageSize={10}
              hideHeaderTools
              emptyText="No data to display"
            />
          </>
        )}
      </div>
      {choosingQc && (
        <AppDialog
          open
          title="QC Type"
          width={360}
          className="dlg-center tmd-24"
          onClose={() => setChoosingQc(false)}
          footer={
            <>
              <button type="button" className="ca-btn ca-btn-solid" disabled={!qcOption}
                onClick={() => { setChoosingQc(false); setPicking('qc'); }}>OK</button>
              <button type="button" className="ca-btn" onClick={() => setChoosingQc(false)}>Cancel</button>
            </>
          }
        >
          <label className="tmd-qccombo" data-unsaved-ignore>
            QC Type:
            <select value={qcType} onChange={(e) => setQcType(e.target.value)} aria-label="QC Type">
              {qcTypes.map((q) => <option key={q.id} value={q.name ?? ''}>{q.name}</option>)}
            </select>
          </label>
        </AppDialog>
      )}
      {picking && (
        <PickListDialog<PickParam>
          title={picking === 'qc' && qcOption ? `Parameter — ${qcOption.name ?? ''}` : 'Parameter'}
          url={`/api/v1/test-methods/${testId}/parameter-picker?kind=${picking}${picking === 'qc' && qcOption ? `&qcType=${qcOption.id}` : ''}`}
          columns={PICK_COLUMNS}
          getId={(r) => r.id}
          onClose={() => setPicking(null)}
          onPicked={picked}
        />
      )}
    </div>
  );
}

// ── Component tab (G5 Component_ListView_Test) ─────────────────────────────────

interface ComponentRow extends Record<string, unknown> {
  oid: string; components: string | null; comment: string | null; retireDate: string | null;
  lastUpdatedDate: string | null; lastUpdatedBy: string | null; parameterCount: number;
}
const COMPONENT_COLUMNS: AppColumn<ComponentRow>[] = [
  { field: 'components', header: 'Components', width: 160 },
  { field: 'comment', header: 'Comment', width: 200 },
  { field: 'retireDate', header: 'RetireDate', width: 110, text: (r) => (r.retireDate ? fmtLocalDate(r.retireDate) : '') },
  { field: 'lastUpdatedDate', header: 'LastUpdatedDate', width: 130, text: (r) => (r.lastUpdatedDate ? fmtLocalDate(r.lastUpdatedDate) : '') },
  { field: 'lastUpdatedBy', header: 'LastUpdatedBy' },
];

/** Component: ADD opens the G5 Component popup (name, comment, retire date, its parameters);
 *  Remove deletes the component with its parameters unless a sample uses one. Both immediate. */
function ComponentTab({ testId }: { testId: string }) {
  const notify = useNotify();
  const { confirm } = useConfirm();
  const [rows, setRows] = useState<ComponentRow[]>([]);
  const [selected, setSelected] = useState<Set<string | number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get<{ data?: ComponentRow[] }>(`/api/v1/test-methods/${testId}/components`);
      setRows(res.data?.data ?? []);
      setSelected(new Set());
    } catch { notify.error('The components could not be loaded.'); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void load(); }, [testId]);

  const remove = async () => {
    if (selected.size === 0) { notify.info('Select checkbox'); return; }
    if (!(await confirm({ title: 'Remove', tone: 'danger', confirmLabel: 'Remove',
      message: 'You are about to remove selected record(s). Do you want to proceed?' }))) return;
    try {
      for (const id of selected) await api.delete(`/api/v1/test-methods/${testId}/components/${id}`);
      notify.success('Deleted Successfully');
    } catch (e) { notify.error(errText(e, 'The component could not be removed.')); }
    await load();
  };

  return (
    <>
      <AppToolbar
        noHotkeys showMeta={false} refreshFirst onRefresh={() => { void load(); }} refreshing={loading}
        crudFirst newLabel="Add" onNew={() => setAdding(true)} deleteLabel="Remove" onDelete={() => { void remove(); }}
        exportData={{ filename: 'Component', ...gridExportMatrix(rows, COMPONENT_COLUMNS) }}
      />
      <AppDataGrid<ComponentRow>
        rows={rows} columns={COMPONENT_COLUMNS} getRowId={(r) => r.oid} loading={loading}
        selectable selectedIds={selected} onSelectionChange={setSelected}
        defaultPageSize={10} hideHeaderTools emptyText="No data to display"
      />
      {adding && <ComponentDialog testId={testId} onClose={() => setAdding(false)} onSaved={() => { setAdding(false); void load(); }} />}
    </>
  );
}

/** G5 Component_DetailView_Test: Component · Comment · RetireDate + its "Test Parameter" grid. */
function ComponentDialog({ testId, onClose, onSaved }: { testId: string; onClose: () => void; onSaved: () => void }) {
  const notify = useNotify();
  const [name, setName] = useState('');
  const [comment, setComment] = useState('');
  const [retire, setRetire] = useState('');
  const [params, setParams] = useState<PickParam[]>([]);
  const [picking, setPicking] = useState(false);
  const [paramSel, setParamSel] = useState<Set<string | number>>(new Set());
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!name.trim()) { notify.warning('Enter the Component'); return; }
    setSaving(true);
    try {
      await api.post(`/api/v1/test-methods/${testId}/components`, {
        components: name.trim(), comment: comment.trim() || null, retireDate: retire || null, parameterIds: params.map((p) => p.id),
      });
      notify.success('Saved successfully.');
      onSaved();
    } catch (e) { notify.error(errText(e, 'The component could not be saved.')); }
    finally { setSaving(false); }
  };

  return (
    <AppDialog open title="Component" width={620} className="dlg-center tmd-24" onClose={onClose}
      footer={<>
        <button type="button" className="ca-btn ca-btn-solid" disabled={saving} onClick={() => { void save(); }}>OK</button>
        <button type="button" className="ca-btn" onClick={onClose}>Cancel</button>
      </>}>
      <div className="tmd-form">
        <label>Component<span className="req">*</span></label>
        <input className="sd-input" value={name} onChange={(e) => setName(e.target.value)} maxLength={100} />
        <label>Comment</label>
        <input className="sd-input" value={comment} onChange={(e) => setComment(e.target.value)} />
        <label>RetireDate</label>
        <input className="sd-input" type="date" value={retire} onChange={(e) => setRetire(e.target.value)} />
      </div>
      <h4 className="tmd-sub">Test Parameter</h4>
      <AppToolbar noHotkeys showMeta={false} crudFirst newLabel="Add"
        onNew={() => { if (!name.trim()) notify.info('Enter the Component Name'); else setPicking(true); }}
        deleteLabel="Remove" onDelete={() => { setParams((ps) => ps.filter((p) => !paramSel.has(p.id))); setParamSel(new Set()); }} />
      <AppDataGrid<PickParam> rows={params} columns={PICK_COLUMNS} getRowId={(r) => r.id}
        selectable selectedIds={paramSel} onSelectionChange={setParamSel} defaultPageSize={10} hideHeaderTools emptyText="No data to display" />
      {picking && (
        <PickListDialog<PickParam>
          title="Parameter"
          url={`/api/v1/test-methods/${testId}/component-parameter-options`}
          columns={PICK_COLUMNS}
          getId={(r) => r.id}
          onClose={() => setPicking(false)}
          onPicked={(ps) => {
            setParams((cur) => [...cur, ...ps.filter((p) => !cur.some((c) => c.id === p.id))]);
            setPicking(false);
          }}
        />
      )}
    </AppDialog>
  );
}
